import React, {useContext, useEffect, useRef, useState} from "react";
import {AuthContext} from "../../components/auth-provider";
import {
    Card,
    CardContent,
    Typography,
    TextField,
    FormControl,
    Button,
    FormHelperText, Tooltip
} from "@material-ui/core";
import {OutlinedInput, InputLabel, InputAdornment, IconButton} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {darken} from '@material-ui/core/styles/colorManipulator';
import FuseAnimate from "../../components/fuse-animate/fuse-animate";
import {Link} from 'react-router-dom'
import clsx from 'clsx';
import {ErrorOutline, Visibility, VisibilityOff} from "@material-ui/icons";
import {ReactComponent as Logo} from "../../images/phonethru-logo.svg";
import {Controller, useForm} from "react-hook-form";
import e164ify from "../../utils/e164";
import {isValidMobile} from "../../utils/valid";
import mobileLogin from "../../utils/mobile-login";
import ausPhone from "../../utils/aus-phone";
import {AuthService} from "../../services";

const useStyles = makeStyles(theme => ({
    root: {
        background: `linear-gradient(to left, ${theme.palette.primary.dark} 0%, ${darken(
            theme.palette.primary.dark,
            0.5
        )} 100%)`,
        color: theme.palette.primary.contrastText,
        minHeight: '100%',
    },
    welcomeWrapper: {
        paddingTop: 108,
    },
    logo: {
        width: 50,
        height: 51,
        [theme.breakpoints.up('lg')]: {
            width: 75,
            height: 76,
        },
    },
    logoText: {
        marginLeft: 4,
        marginTop: 12,
        width: 175,
        height: 40,
        fill: 'white',
        [theme.breakpoints.up('lg')]: {
            marginTop: 22,
            width: 250,
            height: 57,
        },
    },
    panelLogo: {
        width: 200,
        height: 51,
    },
    panelLogoText: {
        marginLeft: 4,
        marginTop: 15,
        width: 175,
        height: 65,
    },
    loginCard: {
        maxWidth: 400,
    },
    loginContent: {
        padding: 32,
        [theme.breakpoints.up('md')]: {
            padding: 48,
            paddingTop: 128,
        },

    },
    welcomeTitle: {
        fontWeight: 300,
    },
    welcomeText: {
        marginTop: 16,
        lineHeight: 1.5,
    },
    loginTitle: {
        marginBottom: 32,
    },
    loginField: {
        marginBottom: 16,
    },
    loginButton: {
        marginTop: 16,
    },
    loginCheckbox: {
        fontSize: 14,
    },
    formError: {
        marginLeft: 14,
        marginTop: 4,
        marginBottom: 16,
        color: 'red',
    },
    link: {
        color: '#047e9f',
        fontWeight: 500,
        '&:hover': {
            textDecoration: 'underline',
            textDecorationColor: '#047e9f',
        },
    }
}));


// Reducer for local state
const loginReducer = (state, action) => {
    switch (action.type) {
        case 'update':
            return {
                ...state,
                ...action.payload,
            }
        default:
            return state
    }
}

// Hook for accessing local storage via the reducer
const useReducer = (reducer, initialState) => {
    const [state, setState] = useState(initialState)

    function dispatch(action) {
        const nextState = reducer(state, action)
        setState(nextState)
    }

    return [state, dispatch]
}


const LoginPage = ({history, match, location}) => {
    const classes = useStyles();
    const focusRef = useRef();

    const [state, dispatch] = useReducer(loginReducer, {
        view: 'mobile',         // View: 'mobile' or 'password' or 'otp'
        showPassword: false,    // flag for password input: true = display password
        apiError: null,         // API error object
        loginError: '',
        mobileNumber: '',
        loginMethod: '',
        password: '',
        otpId: '',
        otpPassword: '',
    });

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    return (

        <div className={clsx(classes.root, 'flex flex-col flex-auto flex-shrink-0 p-0 md:flex-row md:p-0')}>
            <div
                className={clsx(classes.welcomeWrapper, 'hidden md:flex flex-col flex-grow-0 items-center text-white p-16 text-center md:items-start md:flex-1 md:text-left')}>
                <FuseAnimate animation="transition.expandIn">
                    <div className="flex flex-row items-center mb-8">
                        <Logo className={classes.panelLogo} fill="white" alt="logo" />
                    </div>
                </FuseAnimate>

                <FuseAnimate animation="transition.slideUpIn" delay={300}>
                    <Typography variant="h3" color="inherit" className={classes.welcomeTitle}>
                        Welcome!
                    </Typography>
                </FuseAnimate>

                <FuseAnimate delay={400}>
                    <Typography variant="subtitle1" color="inherit" className={classes.welcomeText}>
                        PhoneThru - a landline number for your mobile
                    </Typography>
                </FuseAnimate>
            </div>

            <FuseAnimate animation={{translateX: [0, '100%']}}
                         complete={() => {focusRef.current && focusRef.current.focus()}}>

                <Card className={clsx(classes.loginCard, 'w-full mx-auto my-auto md:m-0')}>
                    <CardContent className={clsx(classes.loginContent, 'flex flex-col items-center justify-center')}>
                        <div className="flex flex-row items-center p-4 md:hidden">
                            <Logo className={classes.panelLogo} fill="#3f51b5" alt="logo" />
                        </div>

                        <Typography variant="h6" className={classes.loginTitle}>SIGN IN TO YOUR ACCOUNT</Typography>

                        {state.view === 'mobile' ?
                            <MobilePanel state={state} dispatch={dispatch}/>
                            : null
                        }

                        {state.view === 'password' ?
                            <PasswordPanel
                                state={state}
                                dispatch={dispatch}
                                history={history}
                                location={location}
                            />
                            : null
                        }

                        {state.view === 'otp' ?
                            <OTPPanel
                                state={state}
                                dispatch={dispatch}
                                history={history}
                                location={location}
                            />
                            : null
                        }

                    </CardContent>
                </Card>

            </FuseAnimate>
        </div>

    );
}

const MobilePanel = ({state, dispatch}) => {
    const classes = useStyles();
    const focusRef = useRef();
    const {handleSubmit, control, formState:{ isValid, isSubmitting }} = useForm({ mode: 'onChange' });

    const onSubmit = (data, loginMethod) => {

        // Clear any existing login errors
        dispatch({ type:'update', payload:{ loginError: '' } })

        return new Promise((resolve) => {
            if (loginMethod === 'password') {

                // Update our state
                dispatch({
                    type:'update',
                    payload:{
                        mobileNumber: data.mobileNumber,
                        loginMethod: loginMethod,
                        view: 'password'
                    },
                })
                resolve();

            } else if (loginMethod === 'otp') {

                // Call an API to send a OTP to the mobile number
                const login = mobileLogin(data.mobileNumber);
                AuthService.requestOTP(login)
                    .then(result => {
                        dispatch({
                            type:'update',
                            payload:{
                                mobileNumber: data.mobileNumber,
                                loginMethod: loginMethod,
                                otpId: result.otpId,
                                view: 'otp'
                            },
                        })
                    })
                    .catch(error => {
                        if (error.status === 424) {
                            // We weren't able to send the SMS
                            dispatch({
                                type:'update',
                                payload: {
                                    loginError: 'Sorry, we can\'t send a one-time password to your mobile right now. Try again later or login with your password.'
                                },
                            })

                        } else {
                            // Display a general API error
                            dispatch({
                                type:'update',
                                payload: {
                                    loginError: "Sorry, we can't process your login right now. Please try again later."
                                },
                            })
                        }

                    })
                    .finally(() => {
                        resolve();
                    })
            }
        })
    }

    const validateMobile = (value) => {
        return isValidMobile(value) || 'Enter a valid Australian mobile number'
    }

    return (
        <>
            {state.loginError ?
                <div className="flex flex-row rounded bg-red-200 p-3 mb-4">
                    <ErrorOutline fontSize="small" className="pr-1"/>{state.loginError}
                </div>
                : null
            }

            <form
                name="loginMobileForm"
                noValidate
                className="flex flex-col justify-center w-full"
                onSubmit={handleSubmit((data) => onSubmit(data, "password"))}
            >
                <Controller
                    name='mobileNumber'
                    control={control}
                    defaultValue={state.mobileNumber}
                    rules={{
                        required: 'Your mobile number is required',
                        validate: (value) => validateMobile(value),
                    }}
                    render={ ({field: {onChange, value}, fieldState:{error} }) => (
                        <TextField
                            label="Mobile Number"
                            inputRef={focusRef}
                            type="tel"
                            value={value}
                            onChange={onChange}
                            variant="outlined"
                            error={!!error}
                            helperText={error ? error.message : 'Enter your mobile phone number'}
                            className={classes.formField}
                            required
                            fullWidth
                        />
                    )}
                />

                <Button
                    id="loginPasswordButton"
                    // type="submit"
                    variant="contained"
                    color="primary"
                    className={classes.loginButton}
                    aria-label="LOG IN"
                    disabled={!isValid || isSubmitting}
                    onClick={handleSubmit((data) => onSubmit(data, 'password')) }
                >
                    SIGN IN WITH PASSWORD
                </Button>

                <Button
                    id="loginOTPButton"
                    // type="submit"
                    variant="contained"
                    color="primary"
                    className={classes.loginButton}
                    aria-label="LOG IN"
                    disabled={!isValid || isSubmitting}
                    onClick={handleSubmit((data) => onSubmit(data, 'otp')) }
                >
                    {isSubmitting ? state.loginMethod === 'otp' ? 'Sending OTP...' : 'SIGN IN WITH ONE-TIME PASSWORD' : 'SIGN IN WITH ONE-TIME PASSWORD'}
                </Button>

                <div className="flex flex-col items-center justify-center pt-8 pb-3">
                    <span className="font-medium">Don't have an account?</span>
                    <Link className={classes.link} to="/signup">
                        Create an account
                    </Link>
                </div>
            </form>
        </>

    )
}

const PasswordPanel = ({state, dispatch, history, location}) => {
    const classes = useStyles();
    const focusRef = useRef();
    const {handleSubmit, control, formState:{ isValid, isSubmitting }} = useForm({ mode: 'onChange' });
    // authState from AuthContext for login functionality
    const auth = useContext(AuthContext)

    const onSubmit = (data) => {

        // Clear any existing login errors
        dispatch({ type:'update', payload:{ loginError: '' } })

        return new Promise((resolve) => {
            const login = mobileLogin(state.mobileNumber);
            auth.login(login, data.password, true)
                .then(() => {
                    // We've logged in.

                    // If we came here via a router redirection because we weren't logged in previously
                    // then the location.state will contain the location the user was headed for prior to
                    // the redirection.  If the target location is / or /home we'll end up there anyway
                    // when AuthProvider triggers a re-render of AuthenticatedApp.
                    // If the user was headed to another page though, we'll need to send then there manually.
                    if (location.state && location.state.pathname !== '' &&
                        location.state.pathname !== "/" && location.state.pathname !== "/home") {
                        // Navigate to the url
                        history.push(location.state.pathname);
                    }

                })
                .catch(error => {
                    // Set the error message.
                    let message;
                    if (error.status === 401) {
                        message = 'The mobile number and password you entered did not match our records. Please double-check and try again.'
                    } else {
                        message = "Oops!  We can't process your login right now. Please try again later."
                    }
                    dispatch({ type:'update', payload: { loginError: message } })
                })
                .finally(() => {
                    resolve()       // Let RHF know we've finished submitting
                })
        })
    }

    const handleClickShowPassword = () => {
        dispatch({type:'update', payload: {showPassword: !state.showPassword}});
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    return (
        <FuseAnimate
            animation="transition.expandIn"
            delay={100}
            complete={() => {
                focusRef.current && focusRef.current.focus();
            }}
        >
            <div className="w-full">
                {state.loginError ?
                    <div className="flex flex-row rounded bg-red-200 p-3 mb-4">
                        <ErrorOutline fontSize="small" className="pr-1"/>{state.loginError}
                    </div>
                    : null
                }

                <form
                    name="loginPasswordForm"
                    noValidate
                    className="flex flex-col justify-center w-full"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Controller
                        name='password'
                        control={control}
                        defaultValue={state.password}
                        rules={{
                            required: 'Your password is required',
                        }}
                        render={ ({field: {onChange, value}, fieldState:{error} }) => (

                            <FormControl className={clsx(classes.margin, classes.textField)}
                                         variant="outlined"
                                         error={!!error}
                                         required={true}
                            >
                                <InputLabel htmlFor="password">Password</InputLabel>
                                <OutlinedInput
                                    name="password"
                                    // className={classes.loginField}
                                    label="Password"
                                    type={state.showPassword ? 'text' : 'password'}
                                    value={value}
                                    onChange={onChange}
                                    inputRef={focusRef}
                                    variant="outlined"
                                    required={true}
                                    fullWidth
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <Tooltip title={"Show / Hide password"}>
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {state.showPassword ? <Visibility/> : <VisibilityOff/>}
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    }
                                />
                                <FormHelperText id="passwordHelperText">
                                    {error ? error.message : 'Enter your password'}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className={classes.loginButton}
                        aria-label="LOG IN"
                        disabled={!isValid || isSubmitting}
                    >
                        {isSubmitting ? 'Signing in...' : 'SIGN IN'}
                    </Button>

                    <div className="flex flex-col mt-8">
                        <Button
                            color="primary"
                            aria-label="GO BACK"
                            disabled={isSubmitting}
                            onClick={ () => {
                                dispatch({
                                    type: 'update',
                                    payload:{
                                        view: 'mobile',
                                        loginError: '',
                                    },
                                })
                            }}
                        >
                            GO BACK
                        </Button>
                    </div>

                </form>
            </div>
        </FuseAnimate>

    )
}

const OTPPanel = ({state, dispatch, history, location}) => {
    const focusRef = useRef();
    const {handleSubmit, control, formState:{ isValid, isSubmitting }} = useForm({ mode: 'onChange' });
    const [verifyState, setVerifyState] = useState({error: ''})
    const auth = useContext(AuthContext)

    const onSubmit = (data) => {

        // Clear any existing login errors
        dispatch({ type:'update', payload:{ loginError: '' } })

        return new Promise((resolve) => {

            auth.login(state.otpId, data.otpPassword, true, 'otp')
                .then(() => {
                    // We've logged in.

                    // If we came here via a router redirection because we weren't logged in previously
                    // then the location.state will contain the location the user was headed for prior to
                    // the redirection.  If the target location is / or /home we'll end up there anyway
                    // when AuthProvider triggers a re-render of AuthenticatedApp.
                    // If the user was headed to another page though, we'll need to send then there manually.
                    if (location.state && location.state.pathname !== '' &&
                        location.state.pathname !== "/" && location.state.pathname !== "/home") {
                        // Navigate to the url
                        history.push(location.state.pathname);
                    }

                })
                .catch(error => {
                    if (error.data) {
                        setVerifyState({ error: error.data.error })
                    }
                })
                .finally(() => {
                    resolve()       // Let RHF know we've finished submitting
                })
        })
    }

    return (
        <FuseAnimate
            animation="transition.expandIn"
            delay={100}
            complete={() => {
                focusRef.current && focusRef.current.focus();
            }}
        >
            <div>
                <div className="mb-6">
                    { verifyState.error === '' ?
                        <Typography paragraph>
                            We've sent a one-time password (OTP)
                            to <strong>{ausPhone(e164ify(state.mobileNumber))}</strong>. Enter
                            the password here to continue.
                        </Typography>
                        : null
                    }
                    { verifyState.error === 'invalid_attempt' ?
                        <Typography paragraph>
                            <span className="text-red-400">
                                <strong>The one-time password was incorrect.</strong>
                            </span>
                            <br/><br/>
                            Check the password and try again - you have a limited number of tries!
                        </Typography>
                        : null
                    }
                    { verifyState.error === 'expired' ?
                        <Typography paragraph>
                            <span className="text-red-400">
                                <strong>The one-time password has expired.</strong>
                            </span>
                            <br/><br/>
                            You'll need to start again with a new OTP, sorry.
                        </Typography>
                        : null
                    }
                    { verifyState.error === 'too_many_attempts' ?
                        <>
                            <Typography paragraph>
                                <span className="text-red-400">
                                    <strong>The one-time password was incorrect.</strong>
                                </span>
                            </Typography>
                            <Typography paragraph>
                                There have been too many unsuccessful tries for this OTP and it has been disabled.
                            </Typography>
                            <Typography paragraph>
                                You'll need to start again with a new OTP, sorry.
                            </Typography>
                        </>
                        : null
                    }
                </div>
                <form
                    name="verifyCodeForm"
                    noValidate
                    className="flex flex-col justify-center, w-full"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Controller
                        name='otpPassword'
                        control={control}
                        defaultValue={state.otpPassword}
                        rules={{
                            required: 'The one-time password is required',
                            maxLength: {value: 6, message: 'Your password is too long'},
                            minLength: {value: 6, message: 'Please enter the complete 6 digit OTP'},
                        }}
                        maxLength={6}
                        render={ ({field: {onChange, value}, fieldState:{error} }) => (
                            <TextField
                                label="One-Time Password (OTP)"
                                inputRef={focusRef}
                                type="tel"
                                value={value}
                                onChange={onChange}
                                variant="outlined"
                                error={!!error}
                                helperText={error ? error.message : 'Enter your one-time password'}
                                inputProps={{maxLength: 6}}
                                required
                                fullWidth
                            />
                        )}
                    />

                    <div className="flex flex-col mt-4">
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            aria-label="SEND CODE"
                            disabled={!isValid || isSubmitting || (verifyState.error && (verifyState.error !== 'invalid_attempt'))}
                        >
                            {isSubmitting ? 'Signing In...' : 'Next'}
                        </Button>
                    </div>

                    <div className="flex flex-col mt-8">
                        <Button
                            color="primary"
                            aria-label="GO BACK"
                            disabled={isSubmitting}
                            onClick={ () => {
                                dispatch({
                                    type: 'update',
                                    payload:{
                                        view: 'mobile',
                                        loginError: '',
                                    },
                                })
                            }}
                        >
                            GO BACK
                        </Button>
                    </div>
                </form>
            </div>
        </FuseAnimate>
    )
}

export default LoginPage;